import {Type} from '@angular/core';
import {RouteContext} from './route-context';
import {ProfileComponent} from '../profile/profile.component';
import {ItemsComponent} from '../item/items.component';
import {LoginComponent} from '../login/login.component';
import {SignupComponent} from '../signup/signup.component';
import {PaymentComponent} from '../payment/payment.component';
import {CheckoutComponent} from '../checkout/checkout.component';
import {OrdersComponent} from '../orders/orders.component';
import {DropshipComponent} from '../dropship/dropship.component';
import {MessagingComponent} from '../messaging/messaging.component';
import {AdminComponent} from '../admin/admin.component';
import {AddItemComponent} from '../add-item/add-item.component';
import {CompareComponent} from '../shared/listing-browse-ui/rightbar/compare/compare.component';
import {ListingBrowseComponent} from '../category/listing-browse/listing-browse.component';
import {ListingDetailComponent} from '../category/listing-detail/listing-detail.component';
import {CategoryLevelComponent} from '../category/shared/category-level/category-level.component';
import {AddListingFormComponent} from '../add-new-item/listing-form/add-listing-form.component';
import {UpdateListingFormComponent} from '../add-update-item/listing-form/update-listing-form.component';

type ModuleLoader = () => Promise<Record<string, unknown>>;

const DEPTH_SUFFIXES = [
  'subsub',
  'subsubsub',
  'subsubsubsub',
  'subsubsubsubsub',
  'subsubsubsubsubsub',
  'subsubsubsubsubsubsub',
  'subsubsubsubsubsubsubsub',
] as const;

/** Longest suffix first so subsubsub is not mistaken for subsub. */
const DEPTH_SUFFIXES_LONGEST_FIRST = [...DEPTH_SUFFIXES].reverse();

function titleCaseSegment(segment: string): string {
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

function browseModuleEntry(name: string): { loader: ModuleLoader; exportName: string } {
  const exportName = `CategoryLevelBrowse${titleCaseSegment(name)}Module`;
  return {
    loader: () => import(`../category/shared/category-level/lazy/browse/category-level-${name}.module`),
    exportName,
  };
}

function addItemModuleEntry(name: string): { loader: ModuleLoader; exportName: string } {
  const exportName = `CategoryLevelAddItem${titleCaseSegment(name)}Module`;
  return {
    loader: () => import(`../category/shared/category-level/lazy/add-item/category-level-${name}.module`),
    exportName,
  };
}

function browseModulePath(name: string): string {
  return `./category/shared/category-level/lazy/browse/category-level-${name}.module#CategoryLevelBrowse${titleCaseSegment(name)}Module`;
}

function addItemModulePath(name: string): string {
  return `./category/shared/category-level/lazy/add-item/category-level-${name}.module#CategoryLevelAddItem${titleCaseSegment(name)}Module`;
}

export const COMPONENT_REGISTRY: Record<string, Type<unknown>> = {
  MessagingComponent,
  PaymentComponent,
  CheckoutComponent,
  OrdersComponent,
  DropshipComponent,
  SignupComponent,
  LoginComponent,
  ItemsComponent,
  ProfileComponent,
  AdminComponent,
  AddItemComponent,
  CompareComponent,
  ListingBrowseComponent,
  ListingDetailComponent,
  CategoryLevelComponent,
  AddListingFormComponent,
  UpdateListingFormComponent,
};

const APP_MODULE_REGISTRY: Record<string, { loader: ModuleLoader; exportName: string }> = {
  './home/home.module#HomeModule': {
    loader: () => import('../home/home.module'),
    exportName: 'HomeModule',
  },
  './category/home/home.module#HomeModule': {
    loader: () => import('../home/home.module'),
    exportName: 'HomeModule',
  },
  './category/listing-browse/listing-browse.module#ListingBrowseModule': {
    loader: () => import('../category/listing-browse/listing-browse.module'),
    exportName: 'ListingBrowseModule',
  },
  './category/item/item.module#ItemModule': {
    loader: () => import('../item/item.module'),
    exportName: 'ItemModule',
  },
  './category/account/account.module#AccountModule': {
    loader: () => import('../account/account.module'),
    exportName: 'AccountModule',
  },
  './category/admin/admin.module#AdminModule': {
    loader: () => import('../admin/admin.module'),
    exportName: 'AdminModule',
  },
  './category/update-item/update-item.module#Update-itemModule': {
    loader: () => import('../add-update-item/update-item.module'),
    exportName: 'UpdateItemModule',
  },
  './category/add-listing/add-listing.module#Add-listingModule': {
    loader: () => import('../add-new-item/add-item.module'),
    exportName: 'AddItemModule',
  },
  './category/add-item/add-item.module#Add-itemModule': {
    loader: () => import('../add-new-item/add-item.module'),
    exportName: 'AddItemModule',
  },
  './add-new-item/add-item.module#AddItemModule': {
    loader: () => import('../add-new-item/add-item.module'),
    exportName: 'AddItemModule',
  },
  './item/item.module#ItemModule': {
    loader: () => import('../item/item.module'),
    exportName: 'ItemModule',
  },
  './add-update-item/update-item.module#UpdateItemModule': {
    loader: () => import('../add-update-item/update-item.module'),
    exportName: 'UpdateItemModule',
  },
  './account/account.module#AccountModule': {
    loader: () => import('../account/account.module'),
    exportName: 'AccountModule',
  },
};

function buildBrowseContextRegistry(): Record<string, { loader: ModuleLoader; exportName: string }> {
  const registry: Record<string, { loader: ModuleLoader; exportName: string }> = {};
  for (const child of DEPTH_SUFFIXES) {
    const path = browseModulePath(child);
    registry[path] = browseModuleEntry(child);
  }
  return registry;
}

function buildAddItemContextRegistry(prefix: string): Record<string, { loader: ModuleLoader; exportName: string }> {
  const registry: Record<string, { loader: ModuleLoader; exportName: string }> = {};
  for (const name of DEPTH_SUFFIXES) {
    const path = addItemModulePath(name);
    registry[path] = addItemModuleEntry(name);
  }
  return registry;
}

const CONTEXT_MODULE_REGISTRY: Partial<Record<RouteContext, Record<string, { loader: ModuleLoader; exportName: string }>>> = {
  sub: {
    [browseModulePath('subsub')]: browseModuleEntry('subsub'),
  },
  subsub: buildBrowseContextRegistry(),
  subsubsub: buildBrowseContextRegistry(),
  subsubsubsub: buildBrowseContextRegistry(),
  subsubsubsubsub: buildBrowseContextRegistry(),
  subsubsubsubsubsub: buildBrowseContextRegistry(),
  subsubsubsubsubsubsub: buildBrowseContextRegistry(),
  subsubsubsubsubsubsubsub: buildBrowseContextRegistry(),
  'add-item': {
    [addItemModulePath('subsub')]: addItemModuleEntry('subsub'),
  },
  'add-item-subsub': buildAddItemContextRegistry('add-item-subsub'),
  'add-item-subsubsub': buildAddItemContextRegistry('add-item-subsubsub'),
  'add-item-subsubsubsub': buildAddItemContextRegistry('add-item-subsubsubsub'),
  'add-item-subsubsubsubsub': buildAddItemContextRegistry('add-item-subsubsubsubsub'),
  'add-item-subsubsubsubsubsub': buildAddItemContextRegistry('add-item-subsubsubsubsubsub'),
  'add-item-subsubsubsubsubsubsub': buildAddItemContextRegistry('add-item-subsubsubsubsubsubsub'),
  'add-item-subsubsubsubsubsubsubsub': buildAddItemContextRegistry('add-item-subsubsubsubsubsubsubsub'),
};

export function resolveLazyModuleLoader(
  context: RouteContext,
  modulePath: string,
): (() => Promise<unknown>) | null {
  if (context.startsWith('add-item')) {
    return resolveAddItemSubPathLoader(modulePath)
      ?? resolveModuleLoader(context, modulePath);
  }
  return resolveCategorySubPathLoader(modulePath)
    ?? resolveModuleLoader(context, modulePath);
}

export function resolveModuleLoader(
  context: RouteContext,
  modulePath: string,
): (() => Promise<unknown>) | null {
  const registry = context === 'app'
    ? APP_MODULE_REGISTRY
    : CONTEXT_MODULE_REGISTRY[context];
  const entry = registry?.[modulePath];
  if (entry) {
    return () => entry.loader().then(m => m[entry.exportName]);
  }

  if (context === 'app') {
    return resolveCategoryModuleLoader(modulePath);
  }

  for (const name of DEPTH_SUFFIXES_LONGEST_FIRST) {
    if (modulePath.includes(`category-level-${name}.module`)) {
      if (modulePath.includes('/lazy/browse/')) {
        const entry = browseModuleEntry(name);
        return () => entry.loader().then(m => m[entry.exportName]);
      }
      if (modulePath.includes('/lazy/add-item/')) {
        const entry = addItemModuleEntry(name);
        return () => entry.loader().then(m => m[entry.exportName]);
      }
    }
  }

  return null;
}

const CATEGORY_URL_MODULES: Record<string, { loader: ModuleLoader; exportName: string }> = {
  home: {loader: () => import('../home/home.module'), exportName: 'HomeModule'},
  item: {loader: () => import('../item/item.module'), exportName: 'ItemModule'},
  account: {loader: () => import('../account/account.module'), exportName: 'AccountModule'},
  admin: {loader: () => import('../admin/admin.module'), exportName: 'AdminModule'},
  'add-listing': {loader: () => import('../add-new-item/add-item.module'), exportName: 'AddItemModule'},
  'add-item': {loader: () => import('../add-new-item/add-item.module'), exportName: 'AddItemModule'},
  'update-item': {loader: () => import('../add-update-item/update-item.module'), exportName: 'UpdateItemModule'},
};

const DEFAULT_BROWSE_MODULE: { loader: ModuleLoader; exportName: string } =
  {loader: () => import('../category/listing-browse/listing-browse.module'), exportName: 'ListingBrowseModule'};

function resolveCategoryModuleLoader(modulePath: string): (() => Promise<unknown>) | null {
  const match = modulePath.match(/^\.\/category\/([^/]+)\/\1\.module#.+Module$/);
  if (!match) {
    return null;
  }

  const [, url] = match;
  const resolved = CATEGORY_URL_MODULES[url] ?? DEFAULT_BROWSE_MODULE;
  return () => resolved.loader().then(m => m[resolved.exportName]);
}

export function resolveCategorySubPathLoader(modulePath?: string): (() => Promise<unknown>) | null {
  if (!modulePath) {
    return null;
  }
  for (const name of DEPTH_SUFFIXES_LONGEST_FIRST) {
    if (modulePath.includes(`category-level-${name}.module`) && modulePath.includes('/lazy/browse/')) {
      const entry = browseModuleEntry(name);
      return () => entry.loader().then(m => m[entry.exportName]);
    }
  }
  return null;
}

export function resolveAddItemSubPathLoader(modulePath?: string): (() => Promise<unknown>) | null {
  if (!modulePath) {
    return null;
  }
  for (const name of DEPTH_SUFFIXES_LONGEST_FIRST) {
    if (modulePath.includes(`category-level-${name}.module`) && modulePath.includes('/lazy/add-item/')) {
      const entry = addItemModuleEntry(name);
      return () => entry.loader().then(m => m[entry.exportName]);
    }
  }
  return null;
}

export function resolveComponent(componentName: string, strict = false): Type<unknown> | undefined {
  const component = COMPONENT_REGISTRY[componentName];
  if (!component) {
    if (strict) {
      throw new Error(`Unknown component: ${componentName}`);
    }
    console.warn('Unknown component:', componentName);
  }
  return component;
}

export function shouldTransformLoadChildren(_context: RouteContext): boolean {
  return true;
}

export function usesSpreadRoute(_context: RouteContext): boolean {
  return true;
}
